import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, FlaskConical, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { caseStudies } from '../mockData';

export const CaseStudyDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const study = caseStudies.find(s => s.slug === slug);

  if (!study) {
    return (
      <div className="min-h-screen pt-32 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Study Not Found</h1>
          <Link to="/case-studies">
            <Button>Back to Case Studies</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header with Image */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate('/case-studies')}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 mb-6 transition-colors duration-300"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Case Studies</span>
          </button>

          <Badge className="bg-blue-600 text-white mb-4">{study.category}</Badge>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {study.title}
          </h1>
          
          <p className="text-xl text-gray-600 mb-8">
            {study.description}
          </p>

          <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
            <img
              src={study.image}
              alt={study.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Study Details */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="border-gray-200">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">Duration</h3>
                </div>
                <p className="text-2xl font-bold text-gray-900">{study.duration}</p>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">Sample Size</h3>
                </div>
                <p className="text-2xl font-bold text-gray-900">{study.sampleSize}</p>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-2">
                  <FlaskConical className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">Study Type</h3>
                </div>
                <p className="text-lg font-semibold text-gray-900">{study.category}</p>
              </CardContent>
            </Card>
          </div>

          {/* Study Overview */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Study Overview</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-lg text-gray-700 leading-relaxed">
                {study.overview}
              </p>
            </div>
          </div>

          {/* Methodology */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Methodology</h2>
            <Card className="border-gray-200">
              <CardContent className="p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {study.methodology}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Statistical Tests - PLACEHOLDER SECTION */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Statistical Analysis</h2>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <p className="text-gray-700 mb-4">
                <strong>Statistical methods employed in this study:</strong>
              </p>
              <ul className="space-y-3">
                {study.statisticalTests.map((test, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{test}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Placeholder for detailed statistical analysis */}
            <Card className="border-2 border-dashed border-gray-300 bg-gray-50">
              <CardContent className="p-8 text-center">
                <div className="max-w-2xl mx-auto">
                  <FlaskConical className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-3">
                    Detailed Statistical Analysis Section
                  </h3>
                  <p className="text-gray-600 mb-4">
                    This section will contain comprehensive statistical analysis details including:
                  </p>
                  <ul className="text-left text-gray-600 space-y-2 mb-6">
                    <li>• Detailed test results with p-values and confidence intervals</li>
                    <li>• Statistical assumptions and validation checks</li>
                    <li>• Model specifications and parameter estimates</li>
                    <li>• Power analysis and effect sizes</li>
                  </ul>
                  <p className="text-sm text-gray-500 italic">
                    Add your detailed statistical methodology and results here
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Results Tables - PLACEHOLDER SECTION */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Results & Data Tables</h2>
            <Card className="border-2 border-dashed border-gray-300 bg-gray-50">
              <CardContent className="p-8 text-center">
                <div className="max-w-2xl mx-auto">
                  <div className="w-12 h-12 bg-gray-300 rounded mx-auto mb-4 flex items-center justify-center">
                    <span className="text-2xl">📊</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-3">
                    Data Tables & Results Section
                  </h3>
                  <p className="text-gray-600 mb-4">
                    This section will display your study results including:
                  </p>
                  <ul className="text-left text-gray-600 space-y-2 mb-6">
                    <li>• Descriptive statistics tables</li>
                    <li>• Comparative analysis results</li>
                    <li>• Demographic and baseline characteristics</li>
                    <li>• Primary and secondary outcome measures</li>
                    <li>• Subgroup analyses</li>
                  </ul>
                  <p className="text-sm text-gray-500 italic">
                    Insert your data tables and statistical results here
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Visualizations - PLACEHOLDER SECTION */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Data Visualizations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map((index) => (
                <Card key={index} className="border-2 border-dashed border-gray-300 bg-gray-50">
                  <CardContent className="p-8 text-center">
                    <div className="aspect-video bg-gray-200 rounded-lg mb-4 flex items-center justify-center">
                      <span className="text-4xl">📈</span>
                    </div>
                    <h3 className="font-semibold text-gray-700 mb-2">
                      Visualization {index}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Add your charts, graphs, or plots here
                      <br />
                      (e.g., box plots, scatter plots, survival curves, ROC curves)
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-sm text-gray-500 italic text-center mt-4">
              Upload your data visualizations: graphs, charts, forest plots, Kaplan-Meier curves, etc.
            </p>
          </div>

          {/* Key Findings */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Findings</h2>
            <Card className="border-gray-200 bg-blue-50">
              <CardContent className="p-6">
                <ul className="space-y-4">
                  {study.keyFindings.map((finding, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-lg text-gray-700">{finding}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Conclusions - PLACEHOLDER SECTION */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Conclusions & Implications</h2>
            <Card className="border-2 border-dashed border-gray-300 bg-gray-50">
              <CardContent className="p-8">
                <div className="max-w-3xl">
                  <h3 className="text-xl font-semibold text-gray-700 mb-4">
                    Study Conclusions Section
                  </h3>
                  <p className="text-gray-600 mb-4">
                    This section should include:
                  </p>
                  <ul className="text-gray-600 space-y-2 mb-6">
                    <li>• Summary of main findings and their interpretation</li>
                    <li>• Clinical or practical implications</li>
                    <li>• Study limitations and considerations</li>
                    <li>• Future research directions</li>
                    <li>• Recommendations for practice</li>
                  </ul>
                  <p className="text-sm text-gray-500 italic">
                    Add your study conclusions and clinical implications here
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Call to Action */}
          <div className="bg-blue-600 rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              Interested in Similar Analysis?
            </h3>
            <p className="text-blue-100 mb-6">
              Let's discuss how specialized biostatistical expertise can support your research
            </p>
            <Link to="/contact">
              <Button className="bg-white text-blue-600 hover:bg-gray-100">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyDetail;
